import { useState, useContext, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { AppContext } from '../AppContext.tsx';
import SettingsRow from '../SettingsRow.jsx';
import InfoIcon from '../icons/infocircle.svg?react';
import SimpleDropdown from '../SimpleDropdown.jsx';
import Checkbox from '../Checkbox.jsx';

// Animation variants
const ease = [0.4, 0.0, 0.2, 1];

const providerVariants = {
  initial: { opacity: 0, height: 0, y: -12 },
  animate: {
    opacity: 1,
    height: "auto",
    y: 0,
    transition: {
      when: "beforeChildren",
      ease,
      duration: 0.2,
      height: { duration: 0.25 }
    }
  },
  exit: {
    opacity: 0,
    y: -8,
    height: 0,
    transition: {
      when: "afterChildren", // Wait for children to exit first
      ease,
      height: { duration: 0.25 }
    }
  }
};

const itemVariants = {
  initial: { opacity: 0, y: 8 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease }
  },
  exit: {
    opacity: 0,
    scale: 0.98,
    transition: { duration: 0.15, ease }
  }
};

/**
 * ModelPreferences - Controlled component for managing enabled models and favorite model
 * 
 * State is managed by parent (SettingsPage) for unified save flow.
 */
const ModelPreferences = ({ 
  localEnabledModelIds, 
  setLocalEnabledModelIds, 
  localFavoriteModelId, 
  setLocalFavoriteModelId,
  modelsOverride
}) => {
  const { 
    models: contextModels, 
    initializingUI 
  } = useContext(AppContext);
  
  // Use modelsOverride if provided (preview from unsaved keys), otherwise use context models
  const models = modelsOverride ?? contextModels;

  const handleToggleModel = (modelId) => {
    setLocalEnabledModelIds(prev => {
      const isEnabled = prev.includes(modelId);
      if (isEnabled) {
        // Don't allow disabling if it's the last enabled model
        if (prev.length === 1) {
          toast.error('At least one model must be enabled');
          return prev;
        }
        
        // If disabling the favorite, clear it
        if (modelId === localFavoriteModelId) {
          setLocalFavoriteModelId(null);
        }
        
        return prev.filter(id => id !== modelId);
      } else {
        return [...prev, modelId];
      }
    });
  };

  // Handle favorite change from dropdown
  const handleFavoriteChange = (modelId) => {
    setLocalFavoriteModelId(modelId);
    
    // Auto-enable the favorite if not already enabled
    if (modelId && !localEnabledModelIds.includes(modelId)) {
      setLocalEnabledModelIds(prev => [...prev, modelId]);
    }
  };

  const StatusIndicator = ({ tooltip }) => (
    <div className="relative group">
      <InfoIcon className="cursor-help size-4" />
      <div className="absolute right-0 bottom-full mb-spacing-2 w-[256px] p-spacing-2 bg-bg-tooltip text-[12px] text-primary rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 border border-border-default whitespace-pre-line">
        {tooltip}
      </div>
    </div>
  );



  // Map model families to display names
  const providerDisplayNames = {
    'gpt': 'OpenAI',
    'claude': 'Anthropic',
    'gemini': 'Google',
    'grok': 'xAI',
    'openrouter': 'OpenRouter'
  };

  // Group models by provider/family with custom ordering
  const modelsByProvider = useMemo(() => {
    const grouped = {};
    models.forEach(model => {
      const family = model.model_family;
      if (!grouped[family]) {
        grouped[family] = [];
      }
      grouped[family].push(model);
    });
    
    // Return in specific order: Anthropic, OpenAI, xAI, Google, OpenRouter
    const orderedProviders = ['claude', 'gpt', 'grok', 'gemini', 'openrouter'];
    const ordered = {};
    orderedProviders.forEach(provider => {
      if (grouped[provider]) {
        ordered[provider] = grouped[provider];
      }
    });
    
    return ordered;
  }, [models]);

  if (!models || models.length === 0) {
    return (
      <section className="mt-spacing-4">
        <h2 className="text-[20px] font-normal mb-spacing-2">Model Preferences</h2>
        <div className="bg-bg-card rounded-lg border border-border-default p-spacing-4">
          <p className="text-secondary text-dls-body">Loading models...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="mt-spacing-4">
      <h2 className="text-[20px] font-normal mb-spacing-2">Model Preferences</h2>
      
      <div className="bg-bg-card rounded-lg border border-border-default">
        {/* Favorite Model Dropdown */}
        <SettingsRow label="Favorite model" isFirst={true}>
          <div className="flex items-center gap-spacing-2">
            <SimpleDropdown
              value={localFavoriteModelId?.toString() || 'none'}
              onChange={(value) => handleFavoriteChange(value === 'none' ? null : parseInt(value))}
              options={[
                { label: 'None (Random)', value: 'none' },
                ...models.map(model => ({
                  label: model.display_name,
                  value: model.id.toString()
                }))
              ]}
              disabled={initializingUI}
              width="w-[194px]"
              searchable={true}
              searchPlaceholder="Search models..."
            />
            
            <StatusIndicator 
              tooltip="Auto-select this model for new conversations. Choose 'None' for random selection."
            />
          </div>
        </SettingsRow>
        
        {/* Enabled Models - Grouped by Provider */}
        <AnimatePresence mode="popLayout">
          {Object.entries(modelsByProvider).map(([provider, providerModels], providerIndex) => {
            const isLastProvider = providerIndex === Object.keys(modelsByProvider).length - 1;
            const displayName = providerDisplayNames[provider] || provider;
            
            return (
              <motion.div 
                key={provider}
                variants={providerVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                style={{ overflow: 'hidden' }}
                className={!isLastProvider ? 'border-b border-border-divider' : ''}
              >
                {/* Provider Header */}
                <div className="px-spacing-4 pt-spacing-2 pb-spacing-2">
                  <h3 className="text-[14px] font-semibold text-primary">{displayName}</h3>
                </div>
                
                {/* Model Checkboxes */}
                <div className="px-spacing-4 pb-spacing-2 flex flex-wrap gap-spacing-3">
                  {providerModels.map((model, index) => {
                    const isEnabled = localEnabledModelIds.includes(model.id);
                    
                    return (
                      <motion.div
                        key={model.id}
                        variants={itemVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        transition={{ delay: index * 0.04 }}
                        className="w-full sm:w-[calc(50%-6px)] md:w-[calc(33.333%-8px)] lg:w-[calc(25%-9px)]"
                      >
                        <Checkbox
                          id={`model-${model.id}`}
                          checked={isEnabled}
                          onChange={() => handleToggleModel(model.id)}
                          label={model.display_name}
                          disabled={initializingUI}
                        />
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default ModelPreferences;
